import SiteFooter from '@/components/SiteFooter'
import SiteHeader from '@/components/SiteHeader'
import { useNews } from '@/hooks/useNews'
import { useRadioData } from '@/hooks/useRadioData'
import WatermarkedImage from '@/components/WatermarkedImage'
import { articlePath, formatNewsDate, splitNewsBody } from '@/lib/news'
import { useHead } from '@unhead/react'
import {ChevronRight, Share2} from 'lucide-react'
import { useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'

function NewsArticle() {
  const { slug = '' } = useParams()
  const { settings, social } = useRadioData()
  const { articles, loading } = useNews(true)
  const published = useMemo(() => articles.filter((item) => item?.published !== false), [articles])
  const normalizedSlug = decodeURIComponent(slug || '').trim().toLowerCase()
  const article = published.find((item) => (item.slug || '').trim().toLowerCase() === normalizedSlug)
  const currentIndex = published.findIndex((item) => (item.slug || '').trim().toLowerCase() === normalizedSlug)
  const paragraphs = splitNewsBody(article?.body)
  const next = currentIndex >= 0 ? published[currentIndex + 1] || published[0] : null
  const shareLink = typeof window !== 'undefined' ? `${window.location.origin}${articlePath(slug)}` : articlePath(slug)
  const ogImage = article?.featuredImage || settings?.iconUrl || ''
  const title = article ? `${article.title} — ${settings?.stationName || 'Bonita FM'}` : 'Matéria — Bonita FM'
  const description = article?.excerpt || settings?.seoDescription || ''

  useHead({
    title,
    htmlAttrs: { lang: 'pt-BR' },
    meta: [
      { name: 'description', content: description },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:type', content: 'article' },
      { property: 'og:image', content: ogImage },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: ogImage },
    ],
  })

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareLink)
    } catch {
      window.prompt('Copie o link para partilhar:', shareLink)
    }
  }

  return (
    <div className="radio-bg min-h-screen">
      <SiteHeader settings={settings} social={social} />
      <main className="mx-auto max-w-3xl px-4 py-8 lg:px-6">
        {loading ? (
          <div className="overflow-hidden rounded-[28px] bg-white/10">
            <div className="h-56 animate-pulse bg-white/15 sm:h-80" />
            <div className="space-y-3 p-5 sm:p-8">
              <div className="h-4 w-28 animate-pulse rounded-full bg-white/20" />
              <div className="h-8 w-4/5 animate-pulse rounded bg-white/20" />
              <div className="h-4 w-full animate-pulse rounded bg-white/10" />
              <div className="h-4 w-2/3 animate-pulse rounded bg-white/10" />
            </div>
          </div>
        ) : null}
        {!loading && !article ? (
          <div className="rounded-3xl bg-white p-8 text-[#12307a]">
            <h1 className="font-display text-2xl font-black">Matéria não encontrada</h1>
            <Link to="/noticias" className="mt-4 inline-block font-bold text-[#0b2ea8]">Ver notícias</Link>
          </div>
        ) : null}

        {article ? (
          <article className="overflow-hidden rounded-[28px] bg-white text-[#12307a] shadow-xl">
            <WatermarkedImage src={article.featuredImage} alt={article.title} className="aspect-video w-full bg-[#071a63]" imgClassName="h-full w-full object-cover" />
            <div className="p-5 sm:p-8">
              <div className="flex flex-wrap items-center gap-2 text-xs font-bold uppercase tracking-wide text-[#2a63e8]">
                <span className="rounded-full bg-[#edf3ff] px-2.5 py-1">{article.category}</span>
                <span>{formatNewsDate(article.createdAt)}</span>
              </div>
              <h1 className="mt-3 font-display text-2xl font-black leading-tight sm:text-3xl">{article.title}</h1>
              <p className="mt-2 text-sm font-semibold text-slate-500">Por {article.author || 'Redação'}</p>
              <div className="mt-5 space-y-4 text-sm leading-7 text-slate-700 sm:text-base">
                {paragraphs.length ? paragraphs.map((paragraph, index) => (
                  <p key={`${article._id || article.slug}-p-${index}`}>{paragraph}</p>
                )) : (
                  <p>{article.excerpt}</p>
                )}
              </div>
              {(article.gallery || []).length ? (
                <div className="mt-6 grid grid-cols-2 gap-3">
                  {article.gallery?.map((url, index) => (
                    <WatermarkedImage key={`${url}-${index}`} src={url} alt="" className="aspect-video w-full rounded-2xl" imgClassName="h-full w-full object-cover" />
                  ))}
                </div>
              ) : null}

              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/" className="rounded-full bg-[#0b2ea8] px-5 py-2.5 text-sm font-bold text-white">
                  Voltar ao site
                </Link>
                {next && next.slug !== article.slug ? (
                  <Link to={articlePath(next.slug)} className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-5 py-2.5 text-sm font-bold">
                    Ver próxima matéria <ChevronRight size={16} />
                  </Link>
                ) : (
                  <Link to="/noticias" className="rounded-full border border-slate-200 px-5 py-2.5 text-sm font-bold">
                    Ver notícias
                  </Link>
                )}
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(`${article.title} ${shareLink}`)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-[#25d366] px-5 py-2.5 text-sm font-bold text-white"
                >
                  <Share2 size={16} /> WhatsApp
                </a>
                <button type="button" onClick={() => void copyShare()} className="rounded-full border border-slate-200 px-5 py-2.5 text-sm font-bold">
                  Copiar link
                </button>
              </div>
            </div>
          </article>
        ) : null}
      </main>
      <SiteFooter settings={settings} />
    </div>
  )
}

export default NewsArticle
